import { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const auth = axios.create({
  baseURL: API,
  withCredentials: true,
});

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const checkAuth = useCallback(async () => {
    // Skip /me if we're handling a Google auth callback fragment
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    try {
      const { data } = await auth.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await auth.post("/auth/login", { email, password });
    setUser(data);
    await refreshAccount();
    return data;
  };

  const register = async (email, password, name, extras = {}) => {
    const { data } = await auth.post("/auth/register", { email, password, name, ...extras });
    setUser(data);
    await refreshAccount();
    return data;
  };

  const logout = async () => {
    setIsLoggingOut(true);
    try {
      await auth.post("/auth/logout");
    } catch {}
    setUser(null);
    // Release the flag on the next tick so route guards can re-render normally.
    setTimeout(() => setIsLoggingOut(false), 0);
  };

  const refreshAccount = useCallback(async () => {
    try {
      const { data } = await auth.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      setUser(null);
      return null;
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{ user, loading, isLoggingOut, login, register, logout, refreshAccount }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

export function formatApiError(err) {
  const d = err?.response?.data?.detail;
  if (!d) return err?.message || "Something went wrong";
  if (typeof d === "string") return d;
  if (Array.isArray(d))
    return d.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" · ");
  if (typeof d === "object") return d.message || JSON.stringify(d);
  return String(d);
}
